// Pure event-shaping helpers for `sentry.server.config.ts`'s `beforeSend`.
// They live here rather than in the config because importing that module runs
// `Sentry.init()` as a side effect, which makes it untestable.

import type { ErrorEvent } from "@sentry/nextjs"

/**
 * Patterns we deliberately drop server-side before sending to Sentry.
 *
 * Each pattern targets a console.error that the runtime catches and recovers
 * from — the page still renders successfully — so the event has no actionable
 * signal but burns quota. We drop ONLY when the event came through
 * captureConsoleIntegration.
 *
 * Verified non-impacting before adding here:
 * - Stale Turbopack chunks: client triggers a hard reload via Next's
 *   ChunkLoadError handling; transactions on affected routes show 0 5xx.
 */
const DROPPED_CONSOLE_MESSAGE_PATTERNS: RegExp[] = [
  // Turbopack stale-chunk after a deploy — Next handles this with a hard reload.
  /Module \d+ was instantiated because it was required from module \d+, but the module factory is not available/,
  // Next.js App Router RSC streaming interruption — fired to console.error when
  // a soft navigation closes the SSR/RSC streaming connection before it finishes
  // (e.g. same-page router navigation, user aborts). React error #412 appears in
  // breadcrumbs. The page has already rendered; the error is handled and has no
  // stack trace. 0 users impacted (MARKETING-SITE-17D, 2 events, 2026-09-27).
  /^Connection closed\.$/,
  // GTM custom tag polling for the _ga cookie on users with GA blocked/no consent.
  // The tag retries 3 times and calls console.error when it gives up. 0 users impacted;
  // the page renders normally.
  /^GA ID not found after \d+ attempts\./,
  // A bundled third-party library tries to assign to a V8-internal Error's
  // .stack property, which V8 defines without a setter in strict-mode bundles.
  // The throw is caught by captureConsoleIntegration; the page renders fine.
  // (MARKETING-SITE-17G — 1 event, 0 users affected)
  /Cannot assign to read only property 'stack' of object ''/,
]

/**
 * `captureConsoleIntegration` renamed its mechanism between SDK majors: v8 set
 * `console`, v9+ sets `auto.core.capture_console` (see
 * `@sentry/core/build/*\/integrations/captureconsole.js`). The check only knew
 * the v8 name, so from the v10 bump until this fix `isDroppedConsoleNoise`
 * returned false for every event and the Turbopack drop above was silently
 * dead. Accept both names so an SDK bump can't disarm the filter again.
 */
const CONSOLE_MECHANISMS = new Set(["console", "auto.core.capture_console"])

/**
 * `throwOnCmsFailure` (lib/cms-server.ts) throws when a CMS fetch failed, so
 * Next never caches a "not found" shell built from an empty response. That
 * throw is correct and stays — but it is reported in EVERY environment, while
 * the diagnostic report it points at ("CMS fetch failed: <tag>", carrying the
 * path and HTTP status) is production-only: #548 deliberately suppressed CMS
 * warnings outside production, and the throw landed two days later without the
 * matching gate. Outside production that leaves a pointer with no target — an
 * error reading "See Sentry for the underlying error" when no such event was
 * ever sent. That inversion is the whole of MARKETING-SITE-101: 1278 of its
 * 1363 events are preview deploys.
 */
// Positional rather than named groups: the repo's tsconfig target predates
// ES2018, so `(?<name>…)` is a compile error here.
export const CMS_FETCH_WRAPPER =
  /^CMS fetch failed for ([^/]+)\/(.*?) — refusing to cache/

function consoleMessage(event: ErrorEvent): string | null {
  const mechanism = event.exception?.values?.[0]?.mechanism?.type
  if (!mechanism || !CONSOLE_MECHANISMS.has(mechanism)) return null
  return event.exception?.values?.[0]?.value ?? ""
}

/**
 * Drop the wrapper wherever its underlying report is suppressed, so the two
 * agree. Production still reports it — there a CMS failure is real.
 */
export function isDroppedConsoleNoise(
  event: ErrorEvent,
  vercelEnv: string | undefined
): boolean {
  const message = consoleMessage(event)
  if (message === null) return false
  if (vercelEnv !== "production" && CMS_FETCH_WRAPPER.test(message)) return true
  return DROPPED_CONSOLE_MESSAGE_PATTERNS.some((re) => re.test(message))
}

/**
 * `captureMessage` shapes from `reportToSentry` (lib/cms.ts): the collection
 * tag is interpolated into the message, so one outage fans out into one issue
 * per collection.
 */
const CMS_FETCH_FAILED_MESSAGE = /^CMS fetch failed: (.+)$/
const CMS_FETCH_INCOMPLETE_MESSAGE = /^CMS fetch incomplete: (.+)$/

/** Thrown by `fetchCmsBiomarkers` (lib/biomarker-clinical-server.ts). */
const CMS_FETCH_STATUS_URL = /^CMS fetch failed \((\d+)\): (\S+)/

/**
 * Chained cause built in `cmsGetWithStatus` (lib/cms.ts) and attached to the
 * wrapper via `throwOnCmsFailure(…, result.cause)`. On a real linked-error
 * event the wrapper is top-level and decides the fingerprint, so this shape
 * is only consulted when it is itself top-level (never observed — nothing
 * reports `result.cause` standalone — but mapped so it groups if it ever is).
 */
const CMS_INCOMPLETE_PAGE_CAUSE =
  /^CMS incomplete page \(\d+\/\d+ docs\): (.+)$/

/**
 * `reportEmptyServicesConfig(ok=false)` (lib/cms-server.ts): the CMS products
 * fetch itself errored, so this is the same dependency failure as
 * `CMS fetch failed: products` and shares its fingerprint. The `ok=true`
 * "config empty" variant is a content gap (nothing published), not an outage,
 * and deliberately stays ungrouped — like "No CMS category for id".
 */
const CMS_PRODUCTS_CONFIG_FAILED = /^products CMS config failed\b/

/** Fail-loud guard in app/sitemap-entries.ts. */
const CMS_SITEMAP_DEGRADED = /^sitemap: CMS fan-out degraded \(([^)]*)\)/

/** Best-effort collection from a full CMS API URL: `…/api/<collection>…`. */
const CMS_API_COLLECTION = /\/api\/([a-z0-9-]+)/i

function setCmsFingerprint(
  event: ErrorEvent,
  kind: string,
  tags: Record<string, string>,
  extra?: Record<string, unknown>
): void {
  event.fingerprint = ["cms-dependency", kind]
  event.tags = { ...event.tags, ...tags }
  if (extra) event.extra = { ...event.extra, ...extra }
}

/**
 * Group by dependency, not by slug: every event that represents the CMS being
 * unreachable, erroring, or incomplete gets `["cms-dependency", <kind>]` with
 * one of four kinds — `fetch-failed`, `incomplete`, `refuse-not-found-shell`,
 * `sitemap-degraded`. Collection, slug, URL and HTTP status move to tags
 * (`cms_collection`, `cms_slug`, `cms_status`) and extra, where they stay
 * searchable without forking the issue per slug.
 *
 * Matches both `captureException` events and `captureMessage` events (the
 * text in `event.message`). Linked-error chains are scanned top-level first:
 * Sentry orders `exception.values` cause-first, so `values[0]` on a real
 * wrapper event is the chained cause ("CMS incomplete page (N/M docs): faq")
 * and the wrapper itself is LAST — reading only `values[0]` matches nothing
 * (MARKETING-SITE-17M). Grouping itself never drops or downgrades: level,
 * message and stack are untouched, and anything that is not a CMS dependency
 * failure — MISSING_MESSAGE, 404s, sharp, "No CMS category for id", the
 * products-config-empty content gap — passes through unchanged. The
 * `throttleCmsDependencyFailures` filter that runs after this one DOES drop:
 * one CMS outage fans out into one event per render per instance
 * (MARKETING-SITE-18D: 74 events in ~20 s), so past the first event per kind
 * per level per minute the rest are suppressed with an overflow count.
 */
export function groupCmsDependencyFailures(event: ErrorEvent): ErrorEvent {
  const values = event.exception?.values ?? []
  const texts: string[] = []
  for (let i = values.length - 1; i >= 0; i--) {
    texts.push(values[i]?.value ?? "")
  }
  texts.push(event.message ?? "")
  for (const text of texts) {
    // By-slug wrapper throw (lib/cms-server.ts `throwOnCmsFailure`).
    const wrapper = CMS_FETCH_WRAPPER.exec(text)
    if (wrapper) {
      const [, collection, slug] = wrapper
      setCmsFingerprint(
        event,
        "refuse-not-found-shell",
        { cms_collection: collection, cms_slug: slug },
        { cmsCollection: collection, cmsSlug: slug }
      )
      return event
    }
    // HTTP status + full CMS API URL throw.
    const statusUrl = CMS_FETCH_STATUS_URL.exec(text)
    if (statusUrl) {
      const [, status, url] = statusUrl
      const collection = CMS_API_COLLECTION.exec(url)?.[1]
      setCmsFingerprint(
        event,
        "fetch-failed",
        {
          cms_status: status,
          ...(collection ? { cms_collection: collection } : {}),
        },
        { cmsUrl: url }
      )
      return event
    }
    // Per-collection fetch-failed warning; the HTTP status rides in extra.
    const failed = CMS_FETCH_FAILED_MESSAGE.exec(text)
    if (failed) {
      const tags: Record<string, string> = {
        cms_collection: failed[1].trim(),
      }
      const status = event.extra?.status
      if (typeof status === "number" || typeof status === "string") {
        tags.cms_status = String(status)
      }
      setCmsFingerprint(event, "fetch-failed", tags)
      return event
    }
    // Per-collection incomplete-page warning.
    const incomplete = CMS_FETCH_INCOMPLETE_MESSAGE.exec(text)
    if (incomplete) {
      setCmsFingerprint(event, "incomplete", {
        cms_collection: incomplete[1].trim(),
      })
      return event
    }
    // Incomplete-page cause, if ever top-level (see CMS_INCOMPLETE_PAGE_CAUSE).
    const incompleteCause = CMS_INCOMPLETE_PAGE_CAUSE.exec(text)
    if (incompleteCause) {
      setCmsFingerprint(event, "incomplete", {
        cms_collection: incompleteCause[1].trim(),
      })
      return event
    }
    // Services control-plane failure downstream of a CMS fetch error.
    if (CMS_PRODUCTS_CONFIG_FAILED.test(text)) {
      setCmsFingerprint(event, "fetch-failed", { cms_collection: "products" })
      return event
    }
    // Sitemap fan-out guard; the degraded collections stay in extra.
    const sitemap = CMS_SITEMAP_DEGRADED.exec(text)
    if (sitemap) {
      const degraded = sitemap[1]
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
      setCmsFingerprint(
        event,
        "sitemap-degraded",
        {},
        { cmsDegraded: degraded }
      )
      return event
    }
  }
  return event
}

/**
 * Per-process rate limit for CMS dependency events (MARKETING-SITE-18D).
 *
 * One CMS brown-out makes every server render report separately — 74 events
 * in one issue, 425 in another — because nothing on the report path dedupes
 * per outage. Past this filter, one outage costs one event per kind per
 * level per process per minute; the first event of the next window carries
 * the suppressed count in `extra.cmsSuppressedEvents` so the volume is still
 * visible. Only `["cms-dependency", …]` events are throttled — everything
 * else is returned unchanged (same reference).
 */
const CMS_THROTTLE_WINDOW_MS = 60_000

const cmsDependencyThrottle = new Map<
  string,
  { windowStart: number; suppressed: number }
>()

export function throttleCmsDependencyFailures(
  event: ErrorEvent,
  now: number = Date.now()
): ErrorEvent | null {
  if (event.fingerprint?.[0] !== "cms-dependency") return event
  const key = `${event.fingerprint.join("\n")}\n${event.level ?? ""}`
  const state = cmsDependencyThrottle.get(key)
  if (!state || now - state.windowStart >= CMS_THROTTLE_WINDOW_MS) {
    const suppressed =
      state && now - state.windowStart >= CMS_THROTTLE_WINDOW_MS
        ? state.suppressed
        : 0
    if (suppressed > 0) {
      event.extra = { ...event.extra, cmsSuppressedEvents: suppressed }
    }
    cmsDependencyThrottle.set(key, { windowStart: now, suppressed: 0 })
    return event
  }
  state.suppressed += 1
  return null
}

/** Test seam — the throttle map is module state and would otherwise leak. */
export function resetCmsDependencyThrottleForTests(): void {
  cmsDependencyThrottle.clear()
}

/**
 * Browser-extension content scripts inject their own webpack bundle into our
 * page and throw from it — most often an unhandled promise rejection fired
 * after the extension's runtime context is torn down. `@sentry/nextjs`'s
 * rewriteFrames integration rewrites every frame's `filename`, stripping the
 * `chrome-extension://<id>` / `moz-extension://<id>` origin down to `app:///`.
 * By the time `beforeSend` runs the origin is already gone, so the
 * `chrome-extension://` `ignoreErrors` filter can never match these. None of
 * the bundle names below are marketing-site code — our client chunks live under
 * `_next/static/chunks/` — so dropping any event whose stack touches one removes
 * the noise without masking a real app error.
 *
 * Observed injectors:
 * - `injectedScript.bundle.js` — password managers, crypto wallets (MetaMask /
 *   Phantom): `Cannot read properties of undefined (reading 'sendMessage')`
 *   (Sentry #126283416).
 * - `content-main.js` — generic extension content-script entrypoint.
 * - `static/toolbar.js` — Vercel Toolbar extension: `n.target?.matches is not a
 *   function` (Sentry #128429098).
 * - `executors/<n>.js` — extension webpack chunk, property-mangled so the
 *   missing member reads as e.g. `M_ID`; `onunhandledrejection`
 *   (MARKETING-SITE-152). Seen on Edge, single-session bursts, 0 users impacted.
 */
const EXTENSION_INJECTED_BUNDLES: RegExp[] = [
  /injectedScript\.bundle\.js/,
  /content-main\.js/,
  /static\/toolbar\.js/,
  /(?:^|\/)executors\/[^/]+\.js/,
]

export function isExtensionInjectedFrame(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((value) =>
    value.stacktrace?.frames?.some((frame) =>
      EXTENSION_INJECTED_BUNDLES.some((re) => re.test(frame.filename ?? ""))
    )
  )
}

/**
 * An extension content script (`js/content.js`, rewritten to
 * `app:///js/content.js` by rewriteFrames) that calls eval/`new Function` runs
 * under our page CSP, which ships no 'unsafe-eval', so it throws `EvalError`
 * (MARKETING-SITE-186: Edge, every frame `app:///js/content.js`). Narrow on
 * purpose: only an EvalError, and only when no frame is one of our `/_next/`
 * chunks, so an eval attempted by our own bundle still reports.
 */
const EXTENSION_CONTENT_SCRIPT = /(?:^|\/)js\/content\.js/

export function isExtensionEvalError(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  const frames = values.flatMap((v) => v.stacktrace?.frames ?? [])
  return (
    values.some((v) => v.type === "EvalError") &&
    !frames.some((f) => /\/_next\//.test(f.filename ?? "")) &&
    frames.some((f) => EXTENSION_CONTENT_SCRIPT.test(f.filename ?? ""))
  )
}

/**
 * The Ella chat widget embed (`embed_script.js`, served from
 * chat.aheadhealth.com) throws internally when its event-listener handler
 * receives an event with an undefined value and reads `.type` off it:
 * `TypeError: Cannot read properties of undefined (reading 'type')`
 * (MARKETING-SITE-176, 39 events, 0 users affected). The throw site is inside
 * the third-party bundle — reported as `app:///embed_script.js` after
 * `@sentry/nextjs`'s rewriteFrames strips the origin — so there is nothing to
 * fix on our side. Our own client chunks live under `_next/static/chunks/`,
 * so dropping any event whose stack touches the bundle masks nothing real.
 * Frames-only, like `isExtensionInjectedFrame`: `culprit` is computed
 * server-side, so it is never present on the client-side event `beforeSend`
 * sees (and the v10 SDK type no longer carries it).
 */
const ELLA_EMBED_SCRIPT = /(?:^|\/)embed_script\.js/

export function isEllaEmbedNoise(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((value) =>
    value.stacktrace?.frames?.some((frame) =>
      ELLA_EMBED_SCRIPT.test(frame.filename ?? "")
    )
  )
}

/**
 * Google Translate injects its own JavaScript (`translate_http`) when a user views
 * the site through the translate.goog proxy. Those scripts throw internally — the
 * canonical example is `Error: La` (a minified error), caught by our global
 * onunhandledrejection handler (MARKETING-SITE-17H, 1 event, 0 users impacted).
 * Every frame in the stack is Google's code; none is ours. Our own client chunks
 * live under `_next/static/chunks/`, so dropping any event whose stack
 * touches `translate_http` scripts masks nothing real.
 * Frames-only check, same pattern as isExtensionInjectedFrame and isEllaEmbedNoise.
 */
const GOOGLE_TRANSLATE_BUNDLE = /(?:^|\/)translate_http\//

export function isGoogleTranslateNoise(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((value) =>
    value.stacktrace?.frames?.some((frame) =>
      GOOGLE_TRANSLATE_BUNDLE.test(frame.filename ?? "")
    )
  )
}

/**
 * Google Translate's proxy (`<host-with-dashes>.translate.goog`) re-serves our
 * page with its own inline script injected into the document. That script
 * throws minified errors (`Error: pa`, MARKETING-SITE-18F: Chrome iOS, 2 events,
 * 0 users) through `window.onerror` — not a rejection and with no
 * `translate_http/` frame, only one frame at the document URL
 * (`app:///ch/en/biomarkers/homa-index`) — so `isGoogleTranslateNoise` and
 * `isInlineShortRejectionNoise` both miss it. The decisive signal is the page
 * being served from a `*.translate.goog` host (`event.request.url`, set by the
 * SDK from `location.href`); the error is Google's code, not ours.
 *
 * Narrow on purpose: the stack must also carry no `/_next/` frame, so an error
 * thrown from one of our own chunks on a translated page still reports. A real
 * bug in our code also fails on our own origin, where nothing here applies
 * (a short message on www.aheadhealth.com still reports).
 */
const TRANSLATE_PROXY_HOST = /(^|\.)translate\.goog$/i

export function isGoogleTranslateProxyNoise(event: ErrorEvent): boolean {
  const url = event.request?.url
  if (!url) return false
  let host: string
  try {
    host = new URL(url).hostname
  } catch {
    return false
  }
  if (!TRANSLATE_PROXY_HOST.test(host)) return false
  const frames = (event.exception?.values ?? []).flatMap(
    (v) => v.stacktrace?.frames ?? []
  )
  return !frames.some((f) => (f.filename ?? "").includes("/_next/"))
}

/**
 * The global-handlers integration names its unhandled-rejection mechanism
 * `auto.browser.global_handlers.onunhandledrejection` — that is the raw
 * `latestEvent.entries.exception.data.values[0].mechanism.type` on the
 * observed MARKETING-SITE-17E event. The bare `onunhandledrejection` shorthand
 * is kept so the filter still matches if an SDK emits that instead. Both names
 * are exact: the sibling `auto.browser.global_handlers.onerror` must never
 * match, so this stays a Set like `CONSOLE_MECHANISMS`, not a suffix check.
 */
const UNHANDLED_REJECTION_MECHANISMS = new Set([
  "onunhandledrejection",
  "auto.browser.global_handlers.onunhandledrejection",
])

/**
 * Third-party inline/eval'd code (GTM container tags, in-app-browser injected
 * bridges such as the Google Search App WebView shim) executes with the
 * document as its script URL, so its frames report as `app:///<page-path>`
 * with no `/_next/` frame anywhere in the stack. When such a rejection also
 * carries a very short alphabetic message (1–4 chars, e.g. `Error: La`,
 * MARKETING-SITE-17E: GSA 439.4/iOS, 1 event, 0 users), there is nothing
 * actionable on our side: our bundles always appear as
 * `app:///_next/static/chunks/*`, and every first-party inline script on the
 * page is either try/caught or promise-free. The conjunction (short message AND
 * zero chunk frames) makes this tight — either signal alone would be too broad.
 */
export function isInlineShortRejectionNoise(event: ErrorEvent): boolean {
  const value = event.exception?.values?.[0]
  if (value?.type !== "Error") return false
  if (!/^[A-Za-z]{1,4}$/.test(value.value ?? "")) return false
  const mechanism = value.mechanism?.type
  if (!mechanism || !UNHANDLED_REJECTION_MECHANISMS.has(mechanism)) return false
  // null stacktrace: no frames were captured at all — no /_next/ frames by
  // definition, so the conjunction still holds (MARKETING-SITE-17Y: Chrome iOS, iPad).
  if (!value.stacktrace) return true
  const frames = value.stacktrace.frames ?? []
  if (frames.length === 0) return false
  return !frames.some((f) => (f.filename ?? "").includes("/_next/"))
}

/**
 * The crawler/bot classes covered by Sentry's built-in inbound "filter known
 * crawlers/spiders" rule, reproduced here because the marketing-site Sentry
 * plan has no inbound filters configured — see the module-level fix
 * rationale in the 404-beacon / bot-noise change. Case-insensitive; matched
 * against a raw User-Agent string.
 */
const BOT_USER_AGENT_PATTERN =
  /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|HeadlessChrome|phantomjs|Lighthouse|Chrome-Lighthouse|Google Page Speed|GTmetrix|pingdom|uptimerobot|datadog|SemrushBot|AhrefsBot|python-requests|curl|wget|Googlebot|bingbot|Applebot|YandexBot|Baiduspider|Sogou|Bytespider/i

export function isBotUserAgent(ua: string): boolean {
  if (!ua) return false
  return BOT_USER_AGENT_PATTERN.test(ua)
}

/**
 * Server-side equivalent of the client `navigator.userAgent` check in
 * `instrumentation-client.ts` — reads the UA off the event's request
 * headers (case-insensitive key, matching how proxies/Next normalize
 * headers inconsistently) since there is no `navigator` on the server.
 */
export function isBotEvent(event: ErrorEvent): boolean {
  const headers = event.request?.headers
  if (!headers) return false
  const key = Object.keys(headers).find((k) => k.toLowerCase() === "user-agent")
  if (!key) return false
  const ua = headers[key]
  return typeof ua === "string" && isBotUserAgent(ua)
}

/**
 * Obscura (a Deno-based headless scraper) spoofs a Chrome UA, so
 * `isBotUserAgent` misses it, and its emulated DOM lacks real browser APIs
 * (`toggleAttribute`, `decode`, ...), producing `x is not a function` errors
 * (MARKETING-SITE-18M / 18N). Not actionable: there is no human user and the
 * "missing API" is the scraper's, not a gap in any real browser. Real failures
 * still report: every event from a real browser engine passes through, since
 * this only matches runtime frames (`<obscura:bootstrap>`, Deno's `ext:core/`)
 * that no browser produces.
 */
export function isHeadlessRuntimeFrame(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some((v) =>
    (v.stacktrace?.frames ?? []).some((f) => {
      const name = f.filename ?? ""
      return name === "<obscura:bootstrap>" || name.startsWith("ext:core/")
    })
  )
}

/**
 * The Media Trust's ad-verification scanner (Referer `connect.themediatrust.com`)
 * injects `mediafilter.generic-wrapper.min.js` and `setup.js` into the page and
 * throws from them: `DataCloneError: ... HTMLIFrameElement object could not be
 * cloned` (MARKETING-SITE-18S, Mobile Safari, onerror). Not our code — nothing
 * named mediafilter exists in the repo. Matches the mediafilter bundle only;
 * bare `setup.js` is too generic to key on.
 */
const MEDIA_TRUST_BUNDLE = /mediafilter\.[\w.-]*\.js$/

export function isMediaTrustScannerFrame(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some((v) =>
    (v.stacktrace?.frames ?? []).some((f) =>
      MEDIA_TRUST_BUNDLE.test(f.filename ?? f.abs_path ?? "")
    )
  )
}
